/**
 * QueryHistorySection Component
 *
 * Displays a list of recent queries with connection filter,
 * allowing users to view history and rerun queries.
 */

import { useState, useCallback, useMemo, useLayoutEffect } from 'react';
import { ChevronDown, History } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { cn } from '../../../../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import { useQueryHistoryStore } from '../../../query-history/store/queryHistoryStore';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { QueryHistoryItem } from './QueryHistoryItem';
import { EmptyState } from './EmptyState';
import { AnimatedCollapse } from './AnimatedCollapse';
import type { QueryHistoryEntry } from '../../../query-history/types/queryHistoryTypes';
import { safeConnectionName, safeConnectionServer } from '../../../../types/safeConnectionDisplay';

/** Number of history items to display */
const MAX_HISTORY_ITEMS = 20;

type QueryHistorySectionProps = {
  /** Whether the section is expanded */
  isExpanded: boolean;
  /** Callback when section expand/collapse state changes */
  onExpandedChange: (expanded: boolean) => void;
  /** Selected connection ID for filtering (null = all connections) */
  selectedConnectionId: string | null;
  /** Callback when selected connection changes */
  onSelectedConnectionChange: (connectionId: string | null) => void;
};

export function QueryHistorySection({
  isExpanded,
  onExpandedChange,
  selectedConnectionId,
  onSelectedConnectionChange,
}: QueryHistorySectionProps) {
  // Connection store
  const connections = useConnectionStore((state) => state.connections);

  // Query history store
  const historyByContext = useQueryHistoryStore(
    (state) => state.historyByContext
  );

  // Workspace store - for adding tabs
  const addTab = useWorkspaceStore((state) => state.addTab);

  // Track keyboard selection
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // Aggregate history entries from all contexts or filtered by connection
  const historyEntries = useMemo(() => {
    const allEntries: QueryHistoryEntry[] = [];

    for (const [contextKey, entries] of Object.entries(historyByContext)) {
      // Context key format: connectionId:database
      const connectionId = contextKey.split(':')[0];

      // Filter by selected connection if one is selected
      if (selectedConnectionId && connectionId !== selectedConnectionId) {
        continue;
      }

      allEntries.push(...entries);
    }

    // Sort by execution time (most recent first) and limit
    return allEntries
      .sort(
        (a, b) =>
          new Date(b.executedAt).getTime() - new Date(a.executedAt).getTime()
      )
      .slice(0, MAX_HISTORY_ITEMS);
  }, [historyByContext, selectedConnectionId]);

  // Handlers
  const handleToggleExpanded = useCallback(() => {
    onExpandedChange(!isExpanded);
  }, [isExpanded, onExpandedChange]);

  const handleConnectionFilterChange = useCallback(
    (value: string) => {
      onSelectedConnectionChange(value === 'all' ? null : value);
    },
    [onSelectedConnectionChange]
  );

  const handleRunAgain = useCallback(
    (entry: QueryHistoryEntry) => {
      // Find connection info
      const connection = connections.find((c) => c.id === entry.connectionId);

      addTab({
        type: 'query',
        title: 'New Query',
        connectionInfo: connection
          ? {
              connectionId: connection.id,
              connectionName: safeConnectionName(connection),
              serverHost: safeConnectionServer(connection),
              database: entry.database,
            }
          : undefined,
        content: {
          query: entry.sql,
        },
      });
    },
    [addTab, connections]
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (historyEntries.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < historyEntries.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < historyEntries.length) {
            handleRunAgain(historyEntries[selectedIndex]);
          }
          break;
      }
    },
    [historyEntries, selectedIndex, handleRunAgain]
  );

  // Auto-collapse when there are no items
  useLayoutEffect(() => {
    if (historyEntries.length === 0 && isExpanded) {
      onExpandedChange(false);
    }
  }, [historyEntries.length, isExpanded, onExpandedChange]);

  return (
    <div
      className="rounded-lg border bg-surface-raised shadow-sm"
      data-testid="query-history-section"
    >
      {/* Section Header */}
      <Button
        variant="ghost"
        className={cn(
          'w-full flex items-center justify-between px-4 py-3 h-auto',
          isExpanded && 'rounded-b-none'
        )}
        onClick={handleToggleExpanded}
        data-testid="query-history-section-toggle"
      >
        <div className="flex items-center gap-2">
          <History className="h-4 w-4" />
          <span className="text-sm font-medium">Query History</span>
          <span className="text-xs text-muted-foreground tabular-nums">
            ({historyEntries.length}
            {historyEntries.length === MAX_HISTORY_ITEMS ? '+' : ''})
          </span>
        </div>
        <ChevronDown
          className={cn('h-4 w-4 transition-transform duration-200', !isExpanded && '-rotate-90')}
        />
      </Button>

      {/* Section Content */}
      <AnimatedCollapse isOpen={isExpanded}>
        <div className="p-4 pt-2" data-testid="query-history-section-content">
          {/* Connection Filter */}
          <div className="mb-3">
            {/* Key forces clean remount when connections change, avoiding Radix UI's ref infinite loop bug */}
            <Select
              key={`connection-filter-${connections.length}`}
              value={selectedConnectionId ?? 'all'}
              onValueChange={handleConnectionFilterChange}
            >
              <SelectTrigger
                className="w-full"
                data-testid="query-history-connection-filter"
              >
                <SelectValue placeholder="Filter by connection" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Connections</SelectItem>
                {connections.map((connection) => (
                  <SelectItem key={connection.id} value={connection.id}>
                    {safeConnectionName(connection)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* History List */}
          <div
            role="listbox"
            aria-label="Query history"
            className="max-h-[400px] overflow-y-auto rounded-md border border-border/50"
            onKeyDown={handleKeyDown}
            tabIndex={0}
            data-testid="query-history-list"
          >
            {historyEntries.length > 0 ? (
              historyEntries.map((entry, index) => (
                <QueryHistoryItem
                  key={entry.id}
                  entry={entry}
                  onRunAgain={handleRunAgain}
                  isSelected={index === selectedIndex}
                />
              ))
            ) : (
              <EmptyState
                icon={History}
                title="No query history"
                description="Run some queries to see your history"
                testId="query-history-empty-state"
              />
            )}
          </div>
        </div>
      </AnimatedCollapse>
    </div>
  );
}
